import React from 'react';
import Grid from '@mui/material/GridLegacy';
import { Box, Typography, Button, Card, CardContent, Chip, IconButton, Tooltip } from '@mui/material';
import { Add, Edit, Delete, Visibility, Place, Map as MapIcon } from '@mui/icons-material';
import { getImageDisplayUrl } from '../../../config/api';
import { formatCategoryName } from '../constants';

export default function PlacesTab({
  places = [],
  onAddPlace,
  onViewPlace,
  onEditPlace,
  onDeletePlace,
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Places Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onAddPlace}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Add New Place
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        There is no separate &quot;Apply filters&quot; here — the <strong>cards below are the full list</strong> of places loaded from the server. Use <strong>View</strong> on a place to see its MongoDB <strong>_id</strong> for Redemptions (Place ID) and stats.
      </Typography>

      {places.length === 0 ? (
        <Card sx={{
          p: 6,
          textAlign: 'center',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Place sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No places yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Add New Place" to get started
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {places.map((place) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={place._id}>
              <Card sx={{
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                }
              }}>
                {place.images && place.images.length > 0 && (
                  <Box sx={{
                    width: '100%',
                    height: 200,
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }}>
                    <Box
                      component="img"
                      src={getImageDisplayUrl(place.images[place.mainImageIndex || 0])}
                      alt={place.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      display: 'flex',
                      gap: 1
                    }}>
                      <Chip
                        label={place.status === 'accepted' ? 'Active' : place.status}
                        size="small"
                        sx={{
                          bgcolor: place.status === 'accepted' ? 'rgba(46, 125, 50, 0.9)' :
                            place.status === 'pending_approval' ? 'rgba(237, 108, 2, 0.9)' :
                              'rgba(211, 47, 47, 0.9)',
                          color: 'white',
                          fontWeight: 600,
                          backdropFilter: 'blur(10px)'
                        }}
                      />
                    </Box>
                  </Box>
                )}
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {place.name}
                      </Typography>
                      {place.location?.address && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {place.location.address}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {place.category && (
                      <Chip
                        label={formatCategoryName(place.category)}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(102, 126, 234, 0.1)',
                          color: 'primary.main',
                          fontWeight: 500
                        }}
                      />
                    )}
                    <Chip
                      label={place.priceRange}
                      size="small"
                      sx={{
                        bgcolor: place.priceRange === 'free' ? 'rgba(46, 125, 50, 0.1)' :
                          place.priceRange === 'high' ? 'rgba(211, 47, 47, 0.1)' :
                            'rgba(158, 158, 158, 0.1)',
                        color: place.priceRange === 'free' ? 'success.main' :
                          place.priceRange === 'high' ? 'error.main' :
                            'text.secondary',
                        fontWeight: 500
                      }}
                    />
                    {place.totalRatings > 0 && (
                      <Chip
                        label={`⭐ ${place.rating.toFixed ? place.rating.toFixed(1) : place.rating}`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255, 193, 7, 0.1)',
                          color: 'warning.main',
                          fontWeight: 500
                        }}
                      />
                    )}
                  </Box>

                  {place.comment && (
                    <Typography variant="body2" color="text.secondary" sx={{
                      mb: 2,
                      fontStyle: 'italic',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {place.comment}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {(place.location?.googleMapsLink || place.googleMapsLink) && (
                        <Tooltip title="View on Maps">
                          <IconButton
                            size="small"
                            href={place.location?.googleMapsLink || place.googleMapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              bgcolor: 'rgba(102, 126, 234, 0.1)',
                              color: 'primary.main',
                              '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' }
                            }}
                          >
                            <MapIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => onViewPlace(place)}
                          sx={{
                            bgcolor: 'rgba(102, 126, 234, 0.1)',
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' }
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => onEditPlace(place)}
                          sx={{
                            bgcolor: 'rgba(158, 158, 158, 0.1)',
                            color: 'text.secondary',
                            '&:hover': { bgcolor: 'rgba(158, 158, 158, 0.2)' }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDeletePlace(place)}
                          sx={{
                            bgcolor: 'rgba(211, 47, 47, 0.1)',
                            '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.2)' }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {place.images && place.images.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {place.images.length} image{place.images.length > 1 ? 's' : ''}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
